<template>
  <BaseModal :open="open" @update:open="$emit('update:open', $event)">
    <div class="relative flex max-h-full max-w-full flex-col items-center gap-4 bg-black pb-16">
      <Cropper v-if="ready" ref="cropperEl" :src="image" :stencil-component="stencilComponent" class="cropper"/>
      <div class="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2">
        <FormButton v-if="!disableCancel"
                    size="md"
                    color="outline-white"
                    type="button"
                    class="whitespace-nowrap"
                    @click="handleCancel()">
          Cancel
        </FormButton>
        <FormButton size="md"
                    color="secondary"
                    type="button"
                    class="whitespace-nowrap"
                    @click="handleDontCrop()">
          Don't Crop
        </FormButton>
        <FormButton size="md"
                    color="gradient"
                    type="button"
                    @click="handleCrop">
          Crop
        </FormButton>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import BaseModal from "./BaseModal.vue";
import FormButton from "./FormButton.vue";
import {ref, watch} from "vue";
import "vue-advanced-cropper/dist/style.css";
import {Cropper} from "vue-advanced-cropper";

const emits = defineEmits(["update:open", "update:file", "update:image"]);
const props = defineProps({
  open: Boolean,
  file: Object,
  image: String,
  stencilComponent: [Object, Function],
  disableCancel: Boolean,
});

const ready = ref(false);
const cropperEl = ref(null);
const oldImage = ref(props.image);

const convertFileToBase64 = (file) => {
  if (!(file instanceof Blob)) {
    console.error("props.file is not a Blob or File", file);
    return;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const dataURLtoFile = (dataUrl, filename) => {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) u8arr[n] = bstr.charCodeAt(n);

  return new File([u8arr], filename, {type: mime});
};

const handleClose = () => {
  emits("update:open", false);
};

const handleCancel = () => {
  ready.value = false;
  emits("update:file", null);
  emits("update:image", oldImage.value);
  handleClose();
};

const handleCrop = () => {
  const result = cropperEl.value.getResult();
  const dataUrl = result.canvas.toDataURL("image/jpeg");

  const file = dataURLtoFile(dataUrl, "cropped.jpg");
  emits("update:file", file);
  emits("update:image", dataUrl);
  handleClose();
};

const handleDontCrop = async () => {
  emits("update:file", props.file);
  handleClose();
};

watch(() => props.file, async (newFile) => {
  oldImage.value = props.image;

  if (newFile) {
    ready.value = false;
    let base64 = await convertFileToBase64(newFile);
    emits("update:image", base64);

    const img = new Image();
    img.src = base64 + "";

    img.onload = () => ready.value = true;
  }
});
</script>

<style scoped>
.cropper {
  min-width: 50vh;
  min-height: 50vh;
  max-height: 90vh;
  max-width: 90vw;
}

@media (min-width: 640px) {
  .cropper {
    min-width: 32rem;
    min-height: 32rem;
    max-height: calc(100vh - 12rem);
  }
}
</style>

